import * as React from 'react';

import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { type CreateAdditionalServicesFormSchemaValues } from 'src/features/parkingManagement/components/AdditionalServices/AddAdditionalServices';
import AdditionalServicesDetails from 'src/features/parkingManagement/components/AdditionalServices/form/AdditionalServicesDetails';
import AdditionalServicesPricing from 'src/features/parkingManagement/components/AdditionalServices/form/AdditionalServicesPricing';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { Button } from 'src/registry/ui/Button';
import { Calendar } from 'src/registry/ui/Calendar';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Popover, PopoverContent, PopoverTrigger } from 'src/registry/ui/Popover';
import { cn } from 'src/utils';
import { generalMessages } from 'src/utils/messages';

import type { UseFormReturn } from 'react-hook-form';

interface AdditionalServicesFormProps {
    form: UseFormReturn<CreateAdditionalServicesFormSchemaValues>;
    onSubmit: () => void;
}

const AdditionalServicesForm = ({ form, onSubmit }: AdditionalServicesFormProps) => {
    const { formatMessage, formatDate } = useIntl();
    const [openCalendar, setOpenCalendar] = React.useState<boolean>(false);
    const [selectedFromDate, setSelectedFromDate] = React.useState<Date | undefined>(form.getValues('valid_from'));

    const selectedDateToDisplay = () => {
        const today = new Date();

        if (selectedFromDate) {
            return selectedFromDate && selectedFromDate < today ? formatDate(today) : formatDate(selectedFromDate);
        } else {
            return selectedFromDate ? (
                formatDate(selectedFromDate)
            ) : (
                <span className="text-gray-400">{formatMessage(generalMessages.pickADate)}</span>
            );
        }
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                    <FormItem variant="dialog" className="w-full">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...generalMessages.name} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input type="text" {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                    <FormItem variant="dialog" className="w-full">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...parkingManagementMessages.description} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input type="text" {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="valid_from"
                render={() => {
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                <FormattedMessage {...reservationMessages.reservationValidFrom} />
                            </FormLabel>
                            <Popover open={openCalendar} onOpenChange={setOpenCalendar}>
                                <FormControlStack>
                                    <PopoverTrigger asChild>
                                        <Button
                                            id="date"
                                            variant="outline"
                                            className={cn('justify-start text-left text-base font-normal')}
                                            onClick={() => setOpenCalendar(true)}
                                        >
                                            <FontAwesomeIcon
                                                icon={faCalendar}
                                                className="text-primary-700 mr-1 size-4"
                                            />
                                            {selectedDateToDisplay()}
                                        </Button>
                                    </PopoverTrigger>
                                </FormControlStack>
                                <PopoverContent className={cn('w-auto bg-white p-4 shadow-sm')} align="start">
                                    <Calendar
                                        mode="single"
                                        selected={selectedFromDate}
                                        defaultMonth={selectedFromDate}
                                        onSelect={(date) => {
                                            if (date) {
                                                setSelectedFromDate(date);
                                                form.setValue('valid_from', date, {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                });
                                                setOpenCalendar(!openCalendar);
                                            }
                                        }}
                                        disabled={{ before: new Date() }}
                                    />
                                </PopoverContent>
                            </Popover>
                        </FormItem>
                    );
                }}
            />

            <AdditionalServicesDetails />

            <AdditionalServicesPricing />
        </Form>
    );
};

export default AdditionalServicesForm;
